import {
    getDocumentFormKey,
    isDocumentTemplateConfig,
    isDocumentTpsConfig,
    resolveDocumentForRequest,
} from '@/components/Views/Company/File/Documents/document-upload-utils';
import type { DocumentActionRoutes, DocumentUploadErrorResponse, File } from '@/components/Views/Company/File/file.types';
import { ESignEditSource, type ClientOption, type DocumentUploadConfig } from '@/components/Views/Company/File/file.types';
import { useFileDocumentStore } from '@/stores/file-document.store';
import type { Client } from '@/types/closinglock.types';
import type { ComputedRef, Ref } from 'vue';
import { ref } from 'vue';

/**
 * Sets up and controls a new tab for editing a document with eSignature
 * - Depends on the useFileDocumentStore for rest calls and to control a page-blocking
 * loading state
 * - Signals completion when tab is closed
 */
export const useDocumentEsignSetup = ({
    selectedSigners,
    documentConfig,
    branchId,
    routes,
}: {
    selectedSigners: Ref<ClientOption[]>;
    documentConfig: ComputedRef<DocumentUploadConfig>;
    branchId: File['branch_id'];
    routes: DocumentActionRoutes;
}) => {
    const fileDocumentStore = useFileDocumentStore();

    const { getESignEditUrl } = fileDocumentStore;

    const eSignTab = ref<ReturnType<Window['open']>>();
    const checkInterval = ref<ReturnType<typeof setInterval>>();

    const esignEditId = ref<DocumentUploadConfig['esignEditId']>();
    const esignEditUrl = ref<DocumentUploadConfig['esignEditUrl']>();
    const setupError = ref<string>();
    const errorResponse = ref<DocumentUploadErrorResponse>();

    const tearDownESignTab = () => {
        fileDocumentStore.isPreparingESign = false;
        eSignTab.value?.close();
    };

    const monitorESignTabClosure = () => {
        return new Promise<void>(resolve => {
            if (checkInterval.value) {
                clearInterval(checkInterval.value);
            }
            checkInterval.value = setInterval(() => {
                if (eSignTab.value?.closed) {
                    clearInterval(checkInterval.value);
                    tearDownESignTab();
                    resolve();
                }
            }, 100);
        });
    };

    /**
     * Opens a new tab in the loading state and either uses
     * and existing folder edit URL or creates a new one and navigates
     * the new tab there.
     */
    const initiateEsignEditTab = async () => {
        if (!selectedSigners.value.length) {
            setupError.value = 'No signers selected';
            return;
        }

        fileDocumentStore.esignEditError = undefined;
        fileDocumentStore.isPreparingESign = true;

        // Open loading tab first to prevent popup blocking
        eSignTab.value = window.open('/loading', '_blank');
        if (!eSignTab.value) {
            tearDownESignTab();
            setupError.value = 'No signers selected';
            return;
        }

        // Check if we already have an eSign URL - reuse it to preserve edits
        if (documentConfig.value?.esignEditUrl && documentConfig.value?.esignEditId) {
            // Navigate directly to existing URL
            if (!eSignTab.value.closed) {
                eSignTab.value.location.href = documentConfig.value.esignEditUrl;
            }
            await monitorESignTabClosure();
            return { success: true };
        }

        // signerIds preserves user selection order — it is the source of truth for signer sequence.
        const signerEmailsMap: Record<number, Client['email']> = {};
        selectedSigners.value.forEach(signer => {
            if (signer.value && signer?.email) {
                signerEmailsMap[Number(signer.value)] = signer.email;
            }
        });
        const signerIds = selectedSigners.value.map(s => Number(s.id));

        const document = resolveDocumentForRequest(documentConfig.value);
        if (!document) {
            tearDownESignTab();
            setupError.value = 'Document not found for eSign request';
            return;
        }
        const templateId = isDocumentTemplateConfig(documentConfig.value) ? documentConfig.value.template?.id : undefined;
        const source = isDocumentTpsConfig(documentConfig.value) ? ESignEditSource.VENDOR : ESignEditSource.WEB_CREATE;

        // Get eSign edit URL from backend (only for new documents or if URL lost)
        const { data, execute, error, response } = getESignEditUrl(routes, {
            document,
            documentFormKey: getDocumentFormKey(documentConfig.value),
            templateId,
            signerIds,
            signerEmails: signerEmailsMap,
            source,
            branchId,
            existingFolderId: documentConfig.value?.esignEditId || null,
            vendorInfo: isDocumentTpsConfig(documentConfig.value) ? documentConfig.value.vendorInfo : undefined,
        });

        await execute();

        // Surface URL and ID to refs
        esignEditUrl.value = data.value?.url;
        esignEditId.value = data.value?.folderID;

        if (error.value) {
            const castError = (await response.value?.json()) as DocumentUploadErrorResponse;
            fileDocumentStore.isPreparingESign = false;
            errorResponse.value = castError;
            fileDocumentStore.esignEditError = castError;
            eSignTab.value?.close();
            return;
        }

        if (!data.value?.url || !data.value?.folderID) {
            tearDownESignTab();
            return;
        }

        // Navigate tab to eSign URL if the window is not closed
        if (eSignTab.value && !eSignTab.value.closed) {
            eSignTab.value.location.href = data.value.url;
        }

        // Update config with folder ID and URL - Used in the validation to infer if the eSign document was edited
        await monitorESignTabClosure();
    };

    return {
        initiateEsignEditTab,
        esignEditId,
        esignEditUrl,
        setupError,
        errorResponse,
    };
};
